import { Injectable } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { endpoints } from '@app/constants';
import { catchError, mergeMap } from 'rxjs/operators';
import { HttpService } from '@app/core/services/http/http.service';
import {
  VoucherChartEntitlementResponse,
  RedeemTrialVoucherInput,
  RedeemTrialVoucherResponse,
  RedeemVoucherRequest,
  RedeemVoucherResponse,
  RedeemVoucherResult,
  GetAvailableProductsByVoucherResponse
} from './redeem-voucher.models';
import { Globals } from '@app/shared/models';
import { ToastrService } from 'ngx-toastr';
import { getRandomNumber } from '@app/utils';

@Injectable({
  providedIn: 'root',
})
export class RedeemVoucherService {
  public checkCodeValid = new Subject<number>();

  constructor(private http: HttpService, private globals: Globals, private toastrService: ToastrService) { }

  checkCode = (code: string): Observable<object> => {
    return this.http.get({ url: endpoints.getCode + code }).pipe(
      mergeMap(this.handleCheckCodeResponse),
      catchError((error) => {
        this.checkCodeValid.next(getRandomNumber());
        return throwError(error);
      })
    );
  }

  getVoucherIncludeChartEntitlement = (code: string): Observable<VoucherChartEntitlementResponse> => {
    return this.http.get({ url: endpoints.getVoucherIncludeChartEntitlement + code }).pipe(
      mergeMap(this.handleVoucherChartEntitlementResponse),
      catchError((error) => {
        this.checkCodeValid.next(getRandomNumber());
        return throwError(error);
      })
    );
  }

  redeemVoucher = (request: RedeemVoucherRequest): Observable<object> => {
    return this.http.post({ url: endpoints.redeemVoucher, body: request }).pipe(
      mergeMap(this.handleRedeemVoucherResult),
      catchError((err) => {
        this.globals.isDisplayProgressSpinner = false;
        this.toastrService.error(err.error.Message);
        return throwError(err);
      })
    );
  }

  redeemTrialVoucher = (request: RedeemTrialVoucherInput): Observable<object> => {
    return this.http.post({ url: endpoints.redeemTrialVoucher, body: request }).pipe(
      mergeMap(this.handleRedeemTrialVoucherResponse),
      catchError((err) => {
        this.globals.isDisplayProgressSpinner = false;
        this.toastrService.error(err.error.Message);
        return throwError(err);
      })
    );
  }

  expireTrialNow = (chartEntitlementId: string): Observable<object> => {
    return this.http.post({ url: endpoints.expireTrialNow.replace('{id}', chartEntitlementId) }).pipe(
      mergeMap(this.handleEndTrialNow),
      catchError((err) => {
        this.globals.isDisplayProgressSpinner = false;
        this.toastrService.error(err.error.Message);
        return throwError(err);
      })
    );
  }

  endTrial = (chartEntitlementId: string): Observable<object> => {
    return this.http.post({ url: endpoints.endTrial.replace('{id}', chartEntitlementId) }).pipe(
      mergeMap(this.handleEndTrialNow),
      catchError((err) => {
        this.globals.isDisplayProgressSpinner = false;
        this.toastrService.error(err.error.Message);
        return throwError(err);
      })
    );
  }

  async getAvailableProductsByVoucher(code: string): Promise<GetAvailableProductsByVoucherResponse> {
    let availableProductsByVoucherResponse: GetAvailableProductsByVoucherResponse = null;
    await this.http.get({ url: endpoints.getAvailableProductsByVoucher + code })
      .toPromise()
      .catch((error) => {
        this.checkCodeValid.next(getRandomNumber());
        return throwError(error);
      })
      .then((response: GetAvailableProductsByVoucherResponse) => {
        availableProductsByVoucherResponse = response;
      });
    return availableProductsByVoucherResponse;
  }

  private handleCheckCodeResponse(response: RedeemVoucherResponse): Observable<RedeemVoucherResponse> {
    return of(response);
  }

  private handleVoucherChartEntitlementResponse(response: VoucherChartEntitlementResponse): Observable<VoucherChartEntitlementResponse> {
    return of(response);
  }

  private handleRedeemTrialVoucherResponse(response: RedeemTrialVoucherResponse): Observable<RedeemTrialVoucherResponse> {
    return of(response);
  }

  private handleRedeemVoucherResult(response: RedeemVoucherResult): Observable<RedeemVoucherResult> {
    return of(response);
  }

  private handleEndTrialNow(response: any): Observable<any> {
    return of(response);
  }
}
